import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { PagedData, PaginationMeta } from './common';
import type { MemberInvitationResult } from './auth';

export type TeamRole = 'OWNER' | 'ADMIN' | 'MEMBER';

export type Team = {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

type TeamListResponse = { data: Team[] };
type TeamResponse = { data: Team };

export type SystemTeam = {
  id: string;
  name: string;
  description: string | null;
  owner: {
    email: string;
    nickname: string;
  } | null;
  memberCount: number;
  projectCount: number;
  subscription: {
    plan: string;
    status: string;
    seats: number;
    currentPeriodEndAt: string;
    trialEndAt: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
};

type SystemTeamsPagedResponse = {
  data: SystemTeam[];
  meta: PaginationMeta;
};

export type MemberInfo = {
  email: string;
  nickname: string;
};

export type TeamMember = {
  id: string;
  memberId: string;
  teamId: string;
  role: TeamRole;
  member: MemberInfo;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

type TeamMemberResponse = { data: TeamMember };
type TeamMemberListResponse = { data: TeamMember[] };
type TeamMemberInvitationResponse = { data: MemberInvitationResult };

export const fetchTeams = async (token: string): Promise<Team[]> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/teams`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch teams: ${response.status}`);
  }

  return ((await response.json()) as TeamListResponse).data;
};

export const fetchSystemTeamsPaged = async (
  token: string,
  params?: {
    search?: string;
    plan?: 'PERSONAL_PRO' | 'TEAM_PRO' | 'PRO' | 'NONE';
    status?: 'ACTIVE' | 'TRIALING' | 'PAST_DUE' | 'CANCELLED' | 'NONE';
    page?: number;
    pageSize?: number;
  },
): Promise<PagedData<SystemTeam>> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.set('search', params.search);
  if (params?.plan) queryParams.set('plan', params.plan);
  if (params?.status) queryParams.set('status', params.status);
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));

  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/admin/teams${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
    {
      cache: 'no-store',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch system teams: ${response.status}`);
  }

  return (await response.json()) as SystemTeamsPagedResponse;
};

export const createTeam = async (token: string, input: { name: string; description?: string }): Promise<Team> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/teams`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create team: ${response.status}`);
  }

  return ((await response.json()) as TeamResponse).data;
};

export const updateTeam = async (
  token: string,
  teamId: string,
  input: { name?: string; description?: string },
): Promise<Team> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/teams/${teamId}`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update team: ${response.status}`);
  }

  return ((await response.json()) as TeamResponse).data;
};

export const leaveTeam = async (token: string, teamId: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/teams/${teamId}/leave`, {
    method: 'POST',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to leave team: ${response.status}`);
  }
};

export const deleteTeam = async (token: string, teamId: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/teams/${teamId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete team: ${response.status}`);
  }
};

export const fetchTeamById = async (token: string, teamId: string): Promise<Team> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/teams/${teamId}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch team: ${response.status}`);
  }

  return ((await response.json()) as TeamResponse).data;
};

export const fetchTeamMembers = async (token: string, teamId: string): Promise<TeamMember[]> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/teams/${teamId}/members`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch team members: ${response.status}`);
  }

  return ((await response.json()) as TeamMemberListResponse).data;
};

export const removeTeamMember = async (token: string, teamId: string, memberId: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/teams/${teamId}/members/${memberId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to remove team member: ${response.status}`);
  }
};

export const addTeamMember = async (
  token: string,
  teamId: string,
  input: { email: string; role: TeamRole },
): Promise<TeamMember | MemberInvitationResult> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/teams/${teamId}/members`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to add team member: ${response.status}`);
  }

  if (response.status === 202) {
    return ((await response.json()) as TeamMemberInvitationResponse).data;
  }

  return ((await response.json()) as TeamMemberResponse).data;
};

export const updateTeamMemberRole = async (
  token: string,
  teamId: string,
  memberId: string,
  role: TeamRole,
): Promise<TeamMember> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/teams/${teamId}/members/${memberId}`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify({ role }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update member role: ${response.status}`);
  }

  return ((await response.json()) as TeamMemberResponse).data;
};
